import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import clsx from 'clsx';
import type { Element as HtmlReactParseElement } from 'html-react-parser';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import { useMemo, useState } from 'react';

const CategoryFaq = props => {
  const { faq } = props;
  const normalizedFaq = useMemo(() => {
    if (!Array.isArray(faq)) {
      return [];
    }

    return faq.filter(item => item?.question || item?.answer);
  }, [faq]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (normalizedFaq.length === 0) {
    return null;
  }

  return (
    <section className='mt-10 border-t border-solid border-E7DED5 lg_mt-[35px]'>
      {normalizedFaq.map((item, index) => {
        const isOpen = openIndex === index;

        return (
          <div
            key={`${item?.question || 'faq'}-${index}`}
            className='border-b border-solid border-E7DED5'
          >
            <button
              type='button'
              className='flex w-full items-start justify-between py-2.5 text-left lg_items-center lg_py-[14px]'
              onClick={() => {
                setOpenIndex(prev => (prev === index ? null : index));
              }}
            >
              <div className='whitespace-pre-wrap text-[16px] font-medium leading-[1.2] [&_a]_underline'>
                {resolveCategoryFaqRichtext(item?.question || '')}
              </div>
              <span className='flex min-h-[18px] w-[52px] flex-none items-center justify-center lg_min-h-auto '>
                <span
                  className={clsx(
                    'relative block h-[1px] w-3 bg-80583F',
                    'before_absolute before_left-1/2 before_top-1/2 before_h-3 before_w-[1px] before_-translate-x-1/2 before_-translate-y-1/2 before_bg-80583F before_content-[""]',
                    '[transform-duration:300ms] before_transition-transform',
                    isOpen ? 'before_rotate-90' : 'before_rotate-0',
                  )}
                ></span>
              </span>
            </button>

            <div
              className={clsx('duration-300 grid transition-all', isOpen ? 'grid-rows-[1fr] py-4' : 'grid-rows-[0fr]')}
            >
              <div className='overflow-hidden'>
                <div className='whitespace-pre-wrap text-[13px] leading-[1.4] lg_text-[15px] [&_a]_underline'>
                  {resolveCategoryFaqRichtext(item?.answer || '')}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const resolveCategoryFaqRichtext = (content: string) => {
  if (typeof content !== 'string' || !content) return null;

  let ret = content;

  if (ret.indexOf('<div data-content-type="html" data-appearance="default" data-element="main">') > -1) {
    ret = ret.replace(
      /<div data-content-type="html" data-appearance="default" data-element="main">|<\/div>|&gt;|&lt;|&amp;/g,
      function (match) {
        return (
          {
            '<div data-content-type="html" data-appearance="default" data-element="main">': '',
            '</div>': '',
            '&gt;': '>',
            '&lt;': '<',
            '&amp;': '&',
          }[match] || match
        );
      },
    );
  }

  if (ret) {
    const parsed = parse(ret, {
      replace: domNode => {
        if ((domNode as unknown as HtmlReactParseElement).name === 'a') {
          const { attribs, children } = domNode as HtmlReactParseElement;
          let href = attribs.href;

          if (typeof href !== 'string' || !href) {
            return (
              <a
                {...attributesToProps(attribs)}
                href={href}
              >
                {domToReact(children)}
              </a>
            );
          }

          let hrefUrlInstance: null | URL = null;

          try {
            hrefUrlInstance = new URL(href, window.location.origin);
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Invalid URL:', href, error);
            }
          }

          if (
            hrefUrlInstance &&
            (['www.awbridal.com', 'www.awbridal.co.uk', 'www.awbridal.ca'].includes(hrefUrlInstance.hostname) ||
              hrefUrlInstance.hostname === window.location.hostname)
          ) {
            href = hrefUrlInstance.pathname;

            if (process.env.NEXT_PUBLIC_BASE_PATH && href.indexOf(process.env.NEXT_PUBLIC_BASE_PATH + '/') !== 0) {
              href = process.env.NEXT_PUBLIC_BASE_PATH + href;
            }

            href = href.replace(
              websiteEnv.isUnitedKingdom ? '/colors-family/' : '/colours-family/',
              `/${COLORS_FAMILY_KEY}/`,
            );

            if (websiteEnv.isUnitedKingdom || websiteEnv.isAustralia) {
              href = href.replace(/\/fall-wedding-guest-dresses(\/|$)/g, '/autumn-wedding-guest-dresses$1');
            }

            if (href[href.length - 1] === '/') {
              href = href.slice(0, -1);
            }
          }

          return (
            <a
              {...attributesToProps(attribs)}
              href={href}
            >
              {domToReact(children)}
            </a>
          );
        }
      },
    });

    return parsed;
  }

  return null;
};

export default CategoryFaq;
